import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingActionButton, Icon, ShowTypeCard } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { VideoCard } from '../../components/media';
import { LANGUAGES, MUSIC_GENRES, PERFORMANCE_FORMATS, PERFORMANCES, SHOW_TYPES, SIGNATURE_USP, SOCIAL_LINKS, SOCIAL_PROOF } from '../../constants';
import { fetchYouTubeTitle } from '../../utils/helpers';

/**
 * Shows Page - Display show types and live performances
 */
const Shows = () => {
  const navigate = useNavigate();
  const [videoTitles, setVideoTitles] = useState({});
  const [activeVideoId, setActiveVideoId] = useState(null); // Track which video is playing

  // Fetch titles for performances on mount
  useEffect(() => {
    const fetchTitles = async () => {
      const titles = {};
      await Promise.all(
        PERFORMANCES.map(async (video) => {
          titles[video.id] = await fetchYouTubeTitle(video.url);
        })
      );
      setVideoTitles(titles);
    };
    fetchTitles();
  }, []);

  // Process videos with titles
  const processedPerformances = useMemo(() => {
    return PERFORMANCES.map((video) => ({
      ...video,
      title: videoTitles[video.id] || 'Loading...',
    }));
  }, [videoTitles]);

  // Handle video play - stops other videos
  const handleVideoPlay = (videoId) => {
    setActiveVideoId(videoId);
  };

  // Handle video stop
  const handleVideoStop = () => {
    setActiveVideoId(null);
  };

  // Page-specific meta information
  const pageTitle = "The Experience | Matisa - Singer & Guitarist";
  const pageDescription = "Discover Matisa's live music experience - genres, languages, performance formats, and booking options for your next event.";

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/shows" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header with Menu */}
      <PageHeader title="Matisa" />

      {/* Main Content Scroll Area */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Page Hero + USP */}
        <section className="flex flex-col gap-3 p-6 pb-4">
          <h1 className="text-white text-3xl font-bold leading-tight tracking-tight">The Experience</h1>
          <p className="text-gold text-sm font-medium italic">"{SIGNATURE_USP}"</p>
        </section>

        {/* Music Genres Section */}
        <section className="flex flex-col gap-4 px-6 pb-6">
          <div className="flex items-center gap-2">
            <Icon name="library_music" size="sm" className="text-neon-pink" />
            <h2 className="text-white text-lg font-bold tracking-tight">Genres I Perform</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {MUSIC_GENRES.map((genre) => (
              <div 
                key={genre.id}
                className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-neon-pink/30 transition-all cursor-default"
              >
                <Icon name={genre.icon} size="xs" className="text-neon-pink/70" />
                <span className="text-white text-sm font-medium">{genre.name}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Languages Section */}
        <section className="flex flex-col gap-4 px-6 pb-6">
          <div className="flex items-center gap-2">
            <Icon name="translate" size="sm" className="text-primary" />
            <h2 className="text-white text-lg font-bold tracking-tight">Languages</h2>
          </div>
          
          {/* Fluent Languages */}
          <div className="flex flex-col gap-2.5">
            <span className="text-[11px] font-bold text-green-400 uppercase tracking-wider">Fluent</span>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.fluent.map((lang) => (
                <div 
                  key={lang.code}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/25 rounded-xl cursor-default"
                >
                  <span className="text-white text-sm font-semibold">{lang.name}</span>
                  <span className="text-primary/50 text-[11px]">{lang.native}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Can Perform In */}
          <div className="flex flex-col gap-2.5">
            <span className="text-[11px] font-bold text-[#90a9cb] uppercase tracking-wider">On Request</span>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.canPerform.map((lang) => (
                <div 
                  key={lang.code}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all cursor-default"
                >
                  <span className="text-[#90a9cb] text-sm font-medium">{lang.name}</span>
                  <span className="text-[#90a9cb]/50 text-[11px]">{lang.native}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Performance Formats Section */}
        <section className="flex flex-col gap-4 px-6 pb-6">
          <div className="flex items-center gap-2">
            <Icon name="tune" size="sm" className="text-gold" />
            <h2 className="text-white text-lg font-bold tracking-tight">Performance Formats</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {PERFORMANCE_FORMATS.map((format) => (
              <div 
                key={format.id}
                className="flex flex-col gap-1 p-3 bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/20 rounded-xl cursor-default"
              >
                <div className="flex items-center gap-2">
                  <Icon name={format.icon} size="xs" className="text-gold" />
                  <span className="text-white text-sm font-semibold">{format.name}</span>
                </div>
                <span className="text-[#90a9cb]/70 text-[11px] pl-5">{format.description}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="px-6 pb-6">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-transparent to-neon-pink/10 border border-white/10 p-5">
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {SOCIAL_PROOF.stats.map((stat, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <span className="text-white text-2xl font-bold tracking-tight">{stat.value}</span>
                  <span className="text-[#90a9cb] text-[11px] font-medium">{stat.label}</span>
                </div>
              ))}
            </div>
            
            {/* Clientele Link */}
            <button 
              onClick={() => {
                const scrollContainer = document.querySelector('.overflow-y-auto');
                if (scrollContainer) scrollContainer.scrollTo(0, 0);
                navigate('/clientele');
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-primary text-xs font-semibold uppercase tracking-wider transition-all"
            >
              View Clientele
              <Icon name="arrow_forward" size="xs" />
            </button>
          </div>
        </section>

        {/* Divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Recording Studio Teaser - Animated Card */}
        <section className="p-6">
          <button
            onClick={() => {
              const scrollContainer = document.querySelector('.overflow-y-auto');
              if (scrollContainer) scrollContainer.scrollTo(0, 0);
              navigate('/recordings');
            }}
            className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-neon-pink/10 via-background-dark to-primary/10 border border-neon-pink/20 hover:border-neon-pink/40 transition-all duration-500"
          >
            {/* Animated Background Glow - Always Active */}
            <div className="absolute inset-0 bg-gradient-to-r from-neon-pink/0 via-neon-pink/10 to-neon-pink/0 animate-glow-sweep" />
            
            {/* Floating Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-3 left-6 w-1 h-1 rounded-full bg-neon-pink/40 animate-pulse" />
              <div className="absolute top-6 right-10 w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse delay-300" />
              <div className="absolute bottom-4 left-1/3 w-1 h-1 rounded-full bg-gold/40 animate-pulse delay-500" />
              <div className="absolute bottom-6 right-1/4 w-1 h-1 rounded-full bg-neon-pink/30 animate-pulse delay-700" />
            </div>

            {/* Main Content */}
            <div className="relative flex items-center gap-4 p-5">
              {/* Animated Vinyl Icon */}
              <div className="relative flex-shrink-0">
                {/* Outer Glow Ring - Always Active */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-pink via-primary to-neon-pink blur-md opacity-70 animate-spin-slow" style={{ animationDuration: '4s' }} />
                
                {/* Vinyl Record */}
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-gray-800 via-gray-900 to-black shadow-xl animate-spin-slow" style={{ animationDuration: '4s' }}>
                  {/* Grooves */}
                  <div className="absolute inset-1 rounded-full border border-white/10" />
                  <div className="absolute inset-2 rounded-full border border-white/5" />
                  <div className="absolute inset-3 rounded-full border border-white/5" />
                  
                  {/* Center Label */}
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-neon-pink to-primary flex items-center justify-center">
                    <Icon name="music_note" size="xs" className="text-white" />
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-neon-pink/80 text-[10px] font-bold uppercase tracking-widest">Beyond the Stage</span>
                  <div className="flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-neon-pink animate-pulse" />
                    <span className="w-1 h-1 rounded-full bg-neon-pink/60 animate-pulse delay-100" />
                    <span className="w-1 h-1 rounded-full bg-neon-pink/30 animate-pulse delay-200" />
                  </div>
                </div>
                <h3 className="text-neon-pink text-lg font-bold tracking-tight">
                  Matisa Records
                </h3>
                <p className="text-[#90a9cb]/80 text-xs mt-0.5">
                  Studio recordings & playback vocals
                </p>
              </div>

              {/* Arrow Indicator - Always Active */}
              <div className="flex-shrink-0 relative">
                <div className="w-10 h-10 rounded-full bg-neon-pink/20 flex items-center justify-center">
                  <Icon 
                    name="arrow_forward" 
                    size="md" 
                    className="text-neon-pink animate-arrow-bounce" 
                  />
                </div>
                {/* Ripple Effect - Always Active */}
                <div className="absolute inset-0 rounded-full border border-neon-pink/30 animate-ripple" />
              </div>
            </div>

            {/* Bottom Waveform Animation - Always Active */}
            <div className="relative h-6 overflow-hidden border-t border-neon-pink/10">
              <div className="absolute inset-0 flex items-center justify-center gap-[3px] px-6">
                {[...Array(24)].map((_, i) => (
                  <div
                    key={i}
                    className="w-[3px] bg-gradient-to-t from-neon-pink/60 to-neon-pink/20 rounded-full animate-waveform"
                    style={{
                      height: '40%',
                      animationDelay: `${i * 0.05}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          </button>

          {/* Inline Styles for Animations */}
          <style>{`
            @keyframes spin-slow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin-slow {
              animation: spin-slow 3s linear infinite;
            }
            @keyframes waveform {
              0%, 100% { height: 20%; }
              50% { height: 80%; }
            }
            .animate-waveform {
              animation: waveform 0.6s ease-in-out infinite;
            }
            @keyframes glow-sweep {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            .animate-glow-sweep {
              animation: glow-sweep 2s ease-in-out infinite;
            }
            @keyframes arrow-bounce {
              0%, 100% { transform: translateX(0); }
              50% { transform: translateX(4px); }
            }
            .animate-arrow-bounce {
              animation: arrow-bounce 1s ease-in-out infinite;
            }
            @keyframes ripple {
              0% { transform: scale(1); opacity: 0.6; }
              100% { transform: scale(1.5); opacity: 0; }
            }
            .animate-ripple {
              animation: ripple 1.5s ease-out infinite;
            }
            .delay-100 { animation-delay: 0.1s; }
            .delay-200 { animation-delay: 0.2s; }
            .delay-300 { animation-delay: 0.3s; }
            .delay-500 { animation-delay: 0.5s; }
            .delay-700 { animation-delay: 0.7s; }
          `}</style>
        </section>

        {/* Divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Book a Show Section */}
        <section className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Icon name="event" size="sm" className="text-gold" />
              <h2 className="text-white text-lg font-bold tracking-tight">Book a Show</h2>
            </div>
            <p className="text-[#90a9cb] text-xs font-medium pl-6">Pick your event type and let's talk</p>
          </div>
          <div className="flex flex-col gap-3">
            {SHOW_TYPES.map((showType) => (
              <ShowTypeCard
                key={showType.id}
                icon={showType.icon}
                title={showType.title}
                description={showType.description}
                accentColor={showType.accentColor}
                onClick={() => {
                  const scrollContainer = document.querySelector('.overflow-y-auto');
                  if (scrollContainer) scrollContainer.scrollTo(0, 0);
                  navigate(`/enquiry?type=${showType.id}`);
                }}
              />
            ))}
          </div>
        </section>

        {/* Live Performances Section */}
        <section id="videos" className="flex flex-col gap-4 p-6 pb-24">
          <div className="flex items-center justify-between">
            <h2 className="text-white text-lg font-bold tracking-tight">Live Performances</h2>
            <button 
              onClick={() => {
                const scrollContainer = document.querySelector('.overflow-y-auto');
                if (scrollContainer) scrollContainer.scrollTo(0, 0);
                navigate('/videos');
              }}
              className="flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider hover:text-neon-pink transition-colors"
            >
              See All
              <Icon name="arrow_forward" size="xs" />
            </button>
          </div>
          
          {/* Video Grid - 2 columns portrait format */}
          <div className="grid grid-cols-2 gap-3">
            {processedPerformances.map((performance) => (
              <VideoCard
                key={performance.id}
                videoId={performance.id}
                url={performance.url}
                title={performance.title}
                categories={performance.categories}
                isPlaying={activeVideoId === performance.id}
                onPlay={() => handleVideoPlay(performance.id)}
                onStop={handleVideoStop}
              />
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              onClick={() => {
                const scrollContainer = document.querySelector('.overflow-y-auto');
                if (scrollContainer) scrollContainer.scrollTo(0, 0);
                navigate('/videos');
              }}
              className="group flex items-center gap-2 text-primary transition-colors hover:text-neon-pink"
            >
              <span className="text-sm font-semibold">View All Performances</span>
              <Icon name="arrow_forward" size="sm" className="transition-transform group-hover:translate-x-1" />
            </button>
            <a 
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-[#90a9cb] transition-colors hover:text-primary"
            >
              <span className="text-sm font-semibold">Watch more on YouTube</span>
              <Icon name="open_in_new" size="sm" className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </section>
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />
    </div>
  );
};

export default Shows;
